<template>
  <div class="manage-role-dialog">
    <el-dialog
      :model-value="visible"
      align-center
      :before-close="close"
      width="480"
    >
      <template #header>
        <h4>
          {{ isAdd ? '新增':'编辑' }}部门
        </h4>
      </template>

      <el-form
        ref="formRef"
        require-asterisk-position="right"
        class="add-platform-users-form"
        label-position="top"
        :model="form"
        :rules="formRules"
      >
        <el-form-item
          label="上级部门"
          prop="parentId"
          clearable
        >
          <el-select v-model="form.parentId" placeholder="请选择上级部门" clearable :disabled="!isAdd">
            <el-option v-for="(v, idx) in deptList" :key="idx" :value="v?.id" :label="v?.deptName" />
          </el-select>
        </el-form-item>
        <el-form-item
          label="部门名称"
          prop="deptName"
        >
          <el-input v-model="form.deptName" placeholder="请选择部门" clearable maxlength="50" show-word-limit />
        </el-form-item>
      </el-form>
      <div class="flex justify-end">
        <el-button :loading="saveBtnLoading" @click="close">
          取消
        </el-button>
        <el-button type="primary" :loading="saveBtnLoading" @click="handleSubmit">
          确定
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ElMessage, FormInstance, FormRules } from 'element-plus';
import { PropType, ref, computed, watch, nextTick } from 'vue';
import { IDeptTreeItem, IDeptTreeItemParams } from '@core/main/types';
import { deptTreeListManage, queryDeptParentList } from '@core/main';

const emit = defineEmits(['close', 'success']);

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  openType: {
    type: String as PropType<'add' | 'edit'>,
    default: 'add',
  },
  row: {
    type: Object as PropType<IDeptTreeItem>,
    default() {
      return {} as IDeptTreeItem;
    },
  },
});

const saveBtnLoading = ref(false);
const formRef = ref<FormInstance>();
const form = ref({} as IDeptTreeItemParams);
const deptList = ref<IDeptTreeItem[]>([]);

const isAdd = computed(() => props.openType === 'add');

const formRules = ref<FormRules<IDeptTreeItemParams>>({
  deptName: [
    {
      required: true,
      message: '请选择部门名称',
    },
  ],
  parentId: [
    {
      required: true,
      message: '请选择上级部门',
    },
  ],
});

async function doSubmit() {
  saveBtnLoading.value = true;
  try {
    const params = isAdd.value
      ? {
        parentId: form.value.parentId,
        deptName: form.value.deptName,
      }
      : {
        id: props.row.id,
        deptName: form.value.deptName,
      };

    await deptTreeListManage(params, isAdd.value);
    ElMessage.success(`${isAdd.value ? '新增' : '编辑'}成功`);
    close();
    emit('success');
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
  saveBtnLoading.value = false;
}

function handleSubmit() {
  formRef.value.validate((valid) => {
    if (valid) doSubmit();
  });
}

function close() {
  formRef.value?.resetFields();
  for (const key in form.value) {
    form.value[key] = undefined;
  }

  emit('close');
}

async function getDepartmentList() {
  try {
    deptList.value = await queryDeptParentList();
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

watch(
  () => props.visible,
  () => {
    nextTick(() => {
      formRef.value?.resetFields();
      if (props.visible) getDepartmentList();
  
      if (props.visible && !isAdd.value) {
        const { parentId, deptName, id } = props.row;
        
        form.value.parentId = parentId;
        form.value.id = id;
        form.value.deptName = deptName;
      }
    });
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.manage-role-dialog {
  :deep(.el-dialog) {
    padding: 0;

    .el-dialog__header {
      padding: 16px 24px 8px;

      h4 {
        color: #344767;
        font-size: 20px;
        font-weight: 600;
      }

      .el-dialog__headerbtn {
        top: 5px;
      }
    }

    .el-dialog__body {
      padding: 24px 24px 16px;

      .el-form-item {
        &:last-child {
          margin-bottom: 32px;
        }

        .el-form-item__label {
          color: #6B7891;
        }

        label::before {
          margin: 0 !important;
          content: '' !important;
        }
      }
    }

  }

}
</style>
